import React, {forwardRef, ReactNode} from 'react';

import resolve from 'lodash.get';
import {Controller, RegisterOptions, UseFormMethods} from 'react-hook-form/dist/index.ie11';

interface FieldConfig {
    validation?: RegisterOptions;
    defaultValue?: any;
}

interface Props {
    id?: string;
    children?: ReactNode | ReactNode[];
    formData: UseFormMethods;
    onSubmit: (data: Record<string, any>) => void | Promise<void>;
    fieldsConfig?: Record<string, FieldConfig>;
    maxFieldsDepth?: number;
    className?: string;
}

export const Form = forwardRef<HTMLFormElement, Props>(
    ({id, children, formData, onSubmit, fieldsConfig, className, maxFieldsDepth = 5}, ref) => {
        // const mappedChildren = Array.isArray(children) ? children : [children];
        const {handleSubmit, control, reset} = formData;

        const submitForm = (data: Record<string, any>) => {
            for (const key of Object.keys(data)) {
                if (data[key] === '') {
                    data[key] = null;
                }
            }
            onSubmit(data);
        };

        const resetForm = () => {
            if (!fieldsConfig) {
                return;
            }

            const defaultValues: Record<string, any> = {};
            Object.entries(fieldsConfig).forEach(([key, value]: [string, FieldConfig]) => {
                defaultValues[key] = value.defaultValue != null ? value.defaultValue : '';
            });

            reset(defaultValues);
        };

        const mapChildren = (
            children: ReactNode | ReactNode[],
            maxRecursion: number,
            currentRecursion = 0,
        ): any =>
            React.Children.map(children, (child) => {
                if (child && React.isValidElement(child)) {
                    if (child.props.formFieldName) {
                        return (
                            <Controller
                                name={child.props.formFieldName}
                                control={control}
                                rules={
                                    fieldsConfig &&
                                    resolve(fieldsConfig, child.props.formFieldName)?.validation
                                }
                                defaultValue={
                                    fieldsConfig &&
                                    resolve(fieldsConfig, child.props.formFieldName)
                                        ?.defaultValue != null
                                        ? resolve(fieldsConfig, child.props.formFieldName)
                                              ?.defaultValue
                                        : ''
                                }
                                render={({onChange, onBlur, value}) =>
                                    React.cloneElement(child, {
                                        ...{
                                            key: child.props.formFieldName,
                                            ...child.props,
                                            onChange,
                                            onBlur,
                                            value,
                                        },
                                    })
                                }
                            />
                        );
                    }
                    if (
                        React.Children.count(child.props.children) !== 0 &&
                        currentRecursion < maxRecursion
                    ) {
                        return React.cloneElement(
                            child,
                            child.props,
                            mapChildren(child.props.children, maxRecursion, currentRecursion + 1),
                        );
                    }
                    return child;
                }
                return child;
            });

        return (
            <form
                id={id}
                ref={ref}
                className={className}
                onSubmit={handleSubmit(submitForm)}
                onReset={resetForm}
            >
                {children && mapChildren(children, maxFieldsDepth, 0)}
            </form>
        );
    },
);

export {useForm} from 'react-hook-form/dist/index.ie11';
